import { ActivityType, HomeWorkoutSequence, UserProfile, UserWorkoutSession } from 'fitbeat.gym-core';
import { CardioOrResistance, GymTypes, IBodyDetails, IHeartRate, IHeartRateMonitors } from 'fitbeat.models';
import { HeartRateStep } from 'fitbeat.models';
import { Dispatch } from 'redux';
import { getConfig, WAITING_FOR_HR_FROM_CONNECTED_APP_TIMEOUT_IN_MS } from '../config';
import connectedAppsManagerSingleton from '../ConnectedApps';
import { GymResolver } from '../services';
import { analyicsForAction } from '../services/google-analytics/analyicsForAction';
import { Logger } from '../utils/Logger';
import { userUpdatedMetricInfo } from './actions';
import { IAppStore } from './reducer';
import { bluetoothDisabledMessage, BluetoothService } from './utils/BluetoothService';
import { BluetoothServiceSingleton } from './utils/BluetoothServiceSingleton';
import { BLUETOOTH_SCAN_TIMEOUT, IBluetoothService, RetryPolicy } from './utils/IBluetoothService';

export const START_BELT_SCAN = 'workout_heart_rate_scan_start';
export const STOP_BELT_SCAN = 'STOP_BELT_SCAN';

export const HEART_RATE_RECEIVED_SUCCESS = 'HEART_RATE_RECEIVED_SUCCESS';
export const HEART_RATE_RECEIVED_FAILURE = 'HEART_RATE_RECEIVED_FAILURE';
export const HEART_RATE_SCAN_SUCCESS = 'HEART_RATE_SCAN_SUCCESS';
export const SUPERFLUOUS_CONNECTED_APP_HEART_RATE_DATA = 'SUPERFLUOUS_CONNECTED_APP_HEART_RATE_DATA';

export const VERIFY_METRIC_DATA_IS_NOT_STALE = 'VERIFY_METRIC_DATA_IS_NOT_STALE';
export const HEART_RATE_MONITOR_CHANGED = 'HEART_RATE_MONITOR_CHANGED';

export const HEART_RATE_SET_TESTING_STUB = 'HEART_RATE_SET_TESTING_STUB';
export const ADD_HEART_RATE_DEVICE_TO_DISCOVER_LIST = 'ADD_HEART_RATE_DEVICE_TO_DISCOVER_LIST';
export const HEART_RATE_DEVICE_DISCOVER_ERROR = 'HEART_RATE_DEVICE_DISCOVER_ERROR';
export const CLEAR_HEART_RATE_DEVICE_LIST = 'CLEAR_HEART_RATE_DEVICE_LIST';

export const START_HEART_RATE_DEVICE_DISCOVERY = 'START_HEART_RATE_DEVICE_DISCOVERY';
export const SAVE_HEART_RATE_MONITOR_IDENTIFIER = 'SAVE_HEART_RATE_MONITOR_IDENTIFIER';
export const SAVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS = 'SAVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS ';
export const SAVE_HEART_RATE_MONITOR_IDENTIFIER_FAILURE = 'SAVE_HEART_RATE_MONITOR_IDENTIFIER_FAILURE ';
export const REMOVE_HEART_RATE_MONITOR_IDENTIFIER = 'REMOVE_HEART_RATE_MONITOR_IDENTIFIER';
export const REMOVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS = 'REMOVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS';
export const DISCONNECT_HEART_RATE_MONITOR = 'DISCONNECT_HEART_RATE_MONITOR';

export const OPEN_STUDIO_HRM_INSTRUCTIONS_BUTTON_PRESS = 'OPEN_STUDIO_HRM_INSTRUCTIONS_BUTTON_PRESS';
export const STUDIO_HRM_INSTRUCTIONS_BACK_BUTTON_PRESS = 'STUDIO_HRM_INSTRUCTIONS_BACK_BUTTON_PRESS';

export const GO_TO_HR_SETUP_STEP = 'GO_TO_HR_SETUP_STEP';
export const SET_THEME_TYPE = 'SET_THEME_TYPE';
export const SET_FAQ_STATE = 'SET_FAQ_STATE';

export const START_WATCH_APP_WORKOUT_REQUEST = 'START_WATCH_APP_WORKOUT_REQUEST';
export const START_WATCH_APP_WORKOUT_TIMER_COMPLETED = 'START_WATCH_APP_WORKOUT_TIMER_COMPLETED';
export const START_WATCH_APP_WORKOUT_REQUEST_FAILURE = 'START_WATCH_APP_WORKOUT_REQUEST_FAILURE';

// TODO: this is not "scan": it is (also) called from HomeWorkouts' unmount
export const stopBeltScanProcess = (destroyBleInstance: boolean) => {
  return async (dispatch: Dispatch<any>) => {
    if (BluetoothServiceSingleton.hasInstanceBeenCreated) {
      const bluetoothService = BluetoothServiceSingleton.getBluetoothService();
      await bluetoothService.stopListeningForHeartRateData();
    }
    await connectedAppsManagerSingleton.stopListenningForHeartRate();
    dispatch(stopBeltScan());
  };
};

export const startBeltScan = (workoutId: string | null) => ({
  type: START_BELT_SCAN,
  payload: {
    workoutId,
  },
  ...analyicsForAction(START_BELT_SCAN),
});

export const stopBeltScan = () => ({
  type: STOP_BELT_SCAN,
});

// todo: extract this into a HR monitor helper
function prioritizeBluetoothHRMonitors(
  callback: (data: IHeartRate) => void,
  unusedConnectedAppCallback: (data: IHeartRate) => void,
) {
  const timeSinceLastDataToConsiderHRBeltDataStale = 10000;

  let lastBluetoothHRDataTime = 0;

  const isBluetoothHRStale = () => {
    const timeSinceLastBluetoothHRData = Date.now() - lastBluetoothHRDataTime;
    return timeSinceLastBluetoothHRData >= timeSinceLastDataToConsiderHRBeltDataStale;
  };

  return (data: IHeartRate) => {
    const isFromBluetoothHR = data.source?.startsWith(BluetoothService.SOURCE_PREFIX);
    if (isFromBluetoothHR) {
      if (data.heartRate > 0) {
        lastBluetoothHRDataTime = data.timestamp.getTime();
      }
      callback(data);
    } else if (isBluetoothHRStale()) {
      callback(data);
    } else {
      unusedConnectedAppCallback(data);
    }
  };
}

export const StartHeartRateTestScan = (
  beltNumber: string,
  bodyDetails: IBodyDetails,
  getBluetoothService: () => IBluetoothService,
  beltScanTimeout: number = 0) => {
  return async (dispatch: any, getState: () => IAppStore) => {
    dispatch(startBeltScan(null));

    const onHrDataReceived = prioritizeBluetoothHRMonitors((heartRateData: IHeartRate) => {
      dispatch(heartRateScanSuccess(heartRateData, bodyDetails));
    }, (connectedAppHRData: IHeartRate) => {
      dispatch(superfluousConnectedAppHeartRateData(connectedAppHRData));
    });

    const onHrDataError = (error: string) => {
      dispatch(heartRateRecievedFailure(error));
    };

    const onHrDisconnected = (isExpected: boolean) => {
      // handle disconnect on user profile side.
    };

    // todo: merge this with StartListeningForHeartRateData / extract a single "start" function
    const useConnectedApps = await connectedAppsManagerSingleton.canUseConnectedAppForHeartRate();
    if (useConnectedApps) {
      connectedAppsManagerSingleton.startListeningForHeartRate(onHrDataReceived)
        .catch((e) => Logger.notify(e));
    }
    getBluetoothService().startListeningForHeartRateDataOn(
      beltNumber, onHrDataReceived, onHrDataError, onHrDisconnected,
    ).catch((e) => Logger.notify(e));
  };
};

const buildHRDataHandlerForHomeWorkouts = (dispatch: any, getState: () => IAppStore, bodyDetails: IBodyDetails) => (
  prioritizeBluetoothHRMonitors((heartRateData: IHeartRate) => {
    const state = getState().homeWorkouts;
    if (!state.homeWorkoutSession) {
      return;
    }
    dispatch(heartRateRecievedSuccess(
      heartRateData, bodyDetails, state.homeWorkoutSession, state.workoutPauseStartTime, state.isPastWarmdown));
  }, (connectedAppHRData: IHeartRate) => {
    dispatch(superfluousConnectedAppHeartRateData(connectedAppHRData));
  })
);

interface IStartWatchAppWorkoutRequest {
  type: typeof START_WATCH_APP_WORKOUT_REQUEST;
  payload: {
    requestId: number,
  },
}

interface IStartWatchAppWorkoutTimerCompleted {
  type: typeof START_WATCH_APP_WORKOUT_TIMER_COMPLETED;
  payload: {
    requestId: number,
  },
}

interface IStartWatchAppWorkoutRequestFailure {
  type: typeof START_WATCH_APP_WORKOUT_REQUEST_FAILURE;
  payload: {
    error: 'Timed out' | string | null;
  }
}

function startWatchAppRequestAndSetupTimer(
  dispatch: Dispatch<
  | IStartWatchAppWorkoutRequest
  | IStartWatchAppWorkoutTimerCompleted
  >,
) {
  const requestId = Date.now();
  dispatch({
    type: START_WATCH_APP_WORKOUT_REQUEST,
    payload: {
      requestId,
    },
  });

  // timeout error handling
  setTimeout(
    () => dispatch({
      type: START_WATCH_APP_WORKOUT_TIMER_COMPLETED,
      payload: {
        requestId,
      },
    }),
    WAITING_FOR_HR_FROM_CONNECTED_APP_TIMEOUT_IN_MS,
  );
}

export const startHomeWatchAppWorkout = async (
  userProfile: UserProfile,
  activityType: ActivityType,
  dispatch: Dispatch<
  | IStartWatchAppWorkoutRequest
  | IStartWatchAppWorkoutTimerCompleted
  | IStartWatchAppWorkoutRequestFailure
  | ISuperfluousConnectedAppHeartRateData
  >,
  getState: () => IAppStore,
) => {
  const canUseConnectedAppsForHR = await connectedAppsManagerSingleton.canUseConnectedAppForHeartRate();
  if (!canUseConnectedAppsForHR) {
    return;
  }

  startWatchAppRequestAndSetupTimer(dispatch);

  try {
    await connectedAppsManagerSingleton.prepareHomeWorkout({
      userId: userProfile.uid,
      activityType,
    });
  } catch (error: any) {
    dispatch({ type: START_WATCH_APP_WORKOUT_REQUEST_FAILURE, payload: { error: error?.message || 'Unknown error' } });
    Logger.leaveBreadcrumb('Failed to prepare workout for connected app', { error });
    // don't return, since it can still recover if watch connects later
  }

  const { homeWorkoutSession } = getState().homeWorkouts;
  const startWatchAppPressedFromHomeWorkoutSetup = !!homeWorkoutSession;
  if (startWatchAppPressedFromHomeWorkoutSetup) {
    const onHrDataReceived = buildHRDataHandlerForHomeWorkouts(dispatch, getState, userProfile);
    await connectedAppsManagerSingleton.startListeningForHeartRate(onHrDataReceived);
  } else {
    await connectedAppsManagerSingleton.startListeningForHeartRate(
      (data: IHeartRate) => dispatch(superfluousConnectedAppHeartRateData(data)),
    );
  }
};

export const startStudioWatchAppWorkout = async (
  activeWorkoutSession: UserWorkoutSession,
  dispatch: Dispatch<
  | IStartWatchAppWorkoutRequest
  | IStartWatchAppWorkoutRequestFailure
  | IStartWatchAppWorkoutTimerCompleted
  | ISuperfluousConnectedAppHeartRateData
  >,
) => {
  const canUseConnectedAppsForHR = await connectedAppsManagerSingleton.canUseConnectedAppForHeartRate();
  if (!canUseConnectedAppsForHR) {
    return;
  }
  startWatchAppRequestAndSetupTimer(dispatch);
  try {
    await connectedAppsManagerSingleton.prepareStudioWorkout({
      userId: activeWorkoutSession.userProfileId,
      activityType: activeWorkoutSession.settings.activityType,
      isoStartTimeOfWarmup: new Date(activeWorkoutSession.startTimeOfFirstExercise).toISOString(),
      isoEndTime: new Date(activeWorkoutSession.endTime).toISOString(),
      startingZone: UserWorkoutSession.getStartingZoneDisplayNumber(activeWorkoutSession),
      hrMetricsServerBaseUrl: getConfig().hrMetricsServer,
    }, (data: IHeartRate) => dispatch(superfluousConnectedAppHeartRateData(data)));
  } catch (error: any) {
    Logger.leaveBreadcrumb('Failed to checkin studio workout for connected app', { error });
    dispatch({ type: START_WATCH_APP_WORKOUT_REQUEST_FAILURE, payload: { error: error?.message || 'Unknown error' }});
  }
};

export const startWatchAppWorkoutPressed = (activeWorkoutSession: UserWorkoutSession, userProfile: UserProfile) => (
  async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
    const gym = GymResolver.getByName(activeWorkoutSession.gymName);
    if (gym.type === GymTypes.Home) {
      await startHomeWatchAppWorkout(userProfile, activeWorkoutSession.settings.activityType, dispatch, getState);
    } else {
      await startStudioWatchAppWorkout(activeWorkoutSession, dispatch);
    }
  }
);

export const StartListeningForHeartRateData = (
  beltNumber = '',
  bodyDetails: IBodyDetails,
  getBlueToothService: () => IBluetoothService,
  beltScanTimeout: number = 0) => {
  return async (dispatch: any, getState: () => IAppStore) => {
    const { homeWorkoutSession } = getState().homeWorkouts;
    if (!homeWorkoutSession) {
      return;
    }

    dispatch(startBeltScan(homeWorkoutSession.workoutId));

    const onHrDataReceived = buildHRDataHandlerForHomeWorkouts(dispatch, getState, bodyDetails);

    const onHrDataError = (error: string) => {
      dispatch(heartRateRecievedFailure(error));
    };

    const onHrDisconnected = (isExpected: boolean) => {
      if (!isExpected) {
        dispatch(disconnectHrDevice());
      }
    };

    // todo: extract a single "start" function for HR monitors
    const useConnectedApps = await connectedAppsManagerSingleton.canUseConnectedAppForHeartRate();
    if (useConnectedApps) {
      await connectedAppsManagerSingleton.startListeningForHeartRate(onHrDataReceived);
    }

    if (!beltNumber || beltNumber.length === 0) {
      return;
    }

    getBlueToothService().startListeningForHeartRateDataOn(
      beltNumber, onHrDataReceived, onHrDataError, onHrDisconnected,
    )
      .catch((e) => Logger.notify(e));
  };
};

export const heartRateScanSuccess = (
  heartRateData: IHeartRate,
  bodyDetails: IBodyDetails,
) => ({
  type: HEART_RATE_SCAN_SUCCESS,
  payload: {
    heartRateData,
    bodyDetails,
  },
});

interface ISuperfluousConnectedAppHeartRateData {
  type: typeof SUPERFLUOUS_CONNECTED_APP_HEART_RATE_DATA;
  payload: {
    heartRateData: IHeartRate;
  }
}

export const superfluousConnectedAppHeartRateData = (
  heartRateData: IHeartRate,
): ISuperfluousConnectedAppHeartRateData => ({
  type: SUPERFLUOUS_CONNECTED_APP_HEART_RATE_DATA,
  payload: {
    heartRateData,
  },
});

export const heartRateRecievedSuccess = (
  heartRateData: IHeartRate,
  bodyDetails: IBodyDetails,
  homeWorkoutSession: HomeWorkoutSequence,
  workoutPauseStartTime: Date | null,
  isPastWarmdown: boolean,
) => ({
  type: HEART_RATE_RECEIVED_SUCCESS,
  payload: {
    heartRateData,
    bodyDetails,
    homeWorkoutSession,
    workoutPauseStartTime,
    isPastWarmdown,
  },
});

export const heartRateRecievedFailure = (error: string) => ({
  type: HEART_RATE_RECEIVED_FAILURE,
  payload: error,
});

export const heartRateMonitorChanged = (userProfile: UserProfile) => {
  return async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
    const bluetoothService: IBluetoothService = BluetoothServiceSingleton.getBluetoothService();
    bluetoothService.setRetryPolicy(RetryPolicy.StopAfterRetryCountExceeded);
    const state = getState();
    const homeWorkoutSession = state.homeWorkouts.homeWorkoutSession;
    if (homeWorkoutSession) {
      await stopBeltScanProcess(false)(dispatch);
      await StartListeningForHeartRateData(
        userProfile.heartRateMonitors?.home,
        userProfile,
        () => bluetoothService,
      )(dispatch, getState);
    }
    dispatch({
      type: HEART_RATE_MONITOR_CHANGED,
      payload: {
        heartRateMonitors: userProfile.heartRateMonitors,
      },
    });
  };
};

export const verifyMetricDataIsNotStale = (
  homeWorkoutSession: HomeWorkoutSequence,
  now: Date,
  workoutPauseStartTime: Date | null,
) => ({
  type: VERIFY_METRIC_DATA_IS_NOT_STALE,
  payload: {
    homeWorkoutSession,
    now,
    workoutPauseStartTime,
  },
});

export const setTestingStub = (useTestingStub) => {
  return {
    type: HEART_RATE_SET_TESTING_STUB,
    payload: {
      useTestingStub,
    },
  };
};

export const startHeartRateDeviceDiscovery = () => ({
  type: START_HEART_RATE_DEVICE_DISCOVERY,
});

export const addHeartRateDeviceToDiscoverList = (device: any) => ({
  type: ADD_HEART_RATE_DEVICE_TO_DISCOVER_LIST,
  payload: {
    ...device,
  },
  ...analyicsForAction('workout_heart_rate_scan_device_found'), // device found
});

export const heartRateDeviceDiscoverError = (error: Error) => {
  return {
    type: HEART_RATE_DEVICE_DISCOVER_ERROR,
    payload: {
      error,
    },
  };
};

export const startScanningForHeartRateDevices = () => {
  return async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
    const bluetoothService = BluetoothServiceSingleton.getBluetoothService();
    const { heartRateMonitor } = getState();
    const { heartRateUseTestingStub } = heartRateMonitor;
    dispatch(startHeartRateDeviceDiscovery());

    if (heartRateUseTestingStub) {
      stubDevices.map((device) => dispatch(addHeartRateDeviceToDiscoverList(device)));
      return;
    }

    const onError = (error: Error) => { dispatch(heartRateDeviceDiscoverError(error)); };
    let timeout = bluetoothService.getDeviceDiscoveryTimeout(BLUETOOTH_SCAN_TIMEOUT, onError);
    const restartTimeout = () => {
      clearTimeout(timeout);
      timeout = bluetoothService.getDeviceDiscoveryTimeout(BLUETOOTH_SCAN_TIMEOUT, onError);
    };

    const onHrDeviceDiscovered = (device: any) => {
      dispatch(addHeartRateDeviceToDiscoverList(device));
      restartTimeout();
    };

    if (bluetoothService != null) {
      const bluetoothIsActive = await bluetoothService.verifyIfBluetoothIsActive();
      if (!bluetoothIsActive) {
        dispatch(heartRateDeviceDiscoverError(new Error(bluetoothDisabledMessage)));
        return;
      }
    }
    await bluetoothService.getDevice(onHrDeviceDiscovered, onError);
  };
};

export const saveHeartRateMonitorIdentifier = () => ({
  type: SAVE_HEART_RATE_MONITOR_IDENTIFIER,
  ...analyicsForAction('workout_heart_rate_scan_connect_request'),
});

export const saveHeartRateMonitorIdentifierSuccess = () => ({
  type: SAVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS,
  ...analyicsForAction('workout_heart_rate_scan_connect_success'),
});

export const saveHeartRateMonitorIdentifierFailure = () => ({
  type: SAVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS,
  ...analyicsForAction('workout_heart_rate_scan_connect_failure'),
});

export const removeHeartRateMonitorIdentifier = () => ({
  type: REMOVE_HEART_RATE_MONITOR_IDENTIFIER,
});

export const removeHeartRateMonitorIdentifierSuccess = () => ({
  type: REMOVE_HEART_RATE_MONITOR_IDENTIFIER_SUCCESS,
  ...analyicsForAction('workout_heart_rate_request_remove'),
});

export const disconnectHrDevice = () => ({
  type: DISCONNECT_HEART_RATE_MONITOR,
  ...analyicsForAction('workout_heart_rate_request_disconnect'),
});

export const clearHeartRateDeviceList = () => ({
  type: CLEAR_HEART_RATE_DEVICE_LIST,
});

export const goToSetupStep = (step) => {
  return {
    type: GO_TO_HR_SETUP_STEP,
    payload: {
      step,
    },
  };
};

export const setThemeType = (themeType) => {
  return {
    type: SET_THEME_TYPE,
    payload: {
      themeType,
    },
  };
};

export const setFaqState = (faqState) => {
  return {
    type: SET_FAQ_STATE,
    payload: {
      ...faqState,
    },
    ...analyicsForAction('workout_heart_rate_scan_faq'),
  };
};

export const getCorrectInitialSetupStep = (userProfile: UserProfile,
                                           initialStepForUsersWithBelts: HeartRateStep = HeartRateStep.TEST_BELT) => {
  return async (dispatch: Dispatch<any>) => {
    const step = UserProfile.hasMetricInfoForHRMonitor(userProfile)
      ? userProfile.heartRateMonitors?.home
        ? initialStepForUsersWithBelts
        : HeartRateStep.CAPTURE_BELT
      : HeartRateStep.CAPTURE_METRIC_INFO;

    dispatch(goToSetupStep(step));
  };
};

export type UpdateHeartRateMonitorsFn = (heartRateMonitors: IHeartRateMonitors) => Promise<UserProfile>;

export const updateUserHomeHRM = (
  homeHRMId: string,
  updateFn: UpdateHeartRateMonitorsFn,
) => (
  async (dispatch: Dispatch<any>) => {
    try {
      dispatch(saveHeartRateMonitorIdentifier());
      const bluetoothService = BluetoothServiceSingleton.getBluetoothService();
      await bluetoothService.stopListeningForHeartRateData();

      const updatedUserProfile = await updateFn({ home: homeHRMId });
      dispatch(userUpdatedMetricInfo(updatedUserProfile));
      dispatch(heartRateMonitorChanged(updatedUserProfile));
      dispatch(saveHeartRateMonitorIdentifierSuccess());
    } catch (e) {
      dispatch({
        type: SAVE_HEART_RATE_MONITOR_IDENTIFIER_FAILURE,
        payload: e,
      });
    }
  }
);

export const updateUserStudioHRM = (
  studioHRMId: string,
  updateFn: UpdateHeartRateMonitorsFn,
) => (
  async (dispatch: Dispatch<any>) => {
    try {
      dispatch(saveHeartRateMonitorIdentifier());
      const updatedUserProfile = await updateFn({ studio: studioHRMId });
      dispatch(userUpdatedMetricInfo(updatedUserProfile));
      dispatch(saveHeartRateMonitorIdentifierSuccess());
    } catch (e) {
      dispatch({
        type: SAVE_HEART_RATE_MONITOR_IDENTIFIER_FAILURE,
        payload: e,
      });
    }
  }
);

export const openStudioHRMInstructionsButtonPress = () => ({
  type: OPEN_STUDIO_HRM_INSTRUCTIONS_BUTTON_PRESS,
});

export const studioHRMInstructionsBackButtonPressed = () => ({
  type: STUDIO_HRM_INSTRUCTIONS_BACK_BUTTON_PRESS,
});

export const removeHeartRateMonitor = (userUpdateFunc: any, userProfile: UserProfile) => {
  return async (dispatch: Dispatch<any>) => {
    const bluetoothService = BluetoothServiceSingleton.getBluetoothService();
    dispatch(removeHeartRateMonitorIdentifier());
    const userProfileCopy = Object.assign({}, userProfile);
    userProfileCopy.heartRateMonitorIdentifier = '';
    const updatedUserProfile = await userUpdateFunc(userProfileCopy);
    dispatch(userUpdatedMetricInfo(updatedUserProfile));
    bluetoothService.stopListeningForHeartRateData()
      .catch((e) => Logger.notify(e));
    // do not disconnect from connected apps: this is affecting Bluetooth Only
    dispatch(removeHeartRateMonitorIdentifierSuccess());
  };
};

export const removeHomeHeartRateMonitor = (updateFn: UpdateHeartRateMonitorsFn) => (
  async (dispatch: Dispatch) => {
    dispatch(removeHeartRateMonitorIdentifier());
    const updatedUserProfile = await updateFn({ home: '' });
    dispatch(userUpdatedMetricInfo(updatedUserProfile));
    const bluetoothService = BluetoothServiceSingleton.getBluetoothService();
    bluetoothService.stopListeningForHeartRateData()
      .catch((e) => Logger.notify(e));
    dispatch(removeHeartRateMonitorIdentifierSuccess());
  }
);

export const disconnectHeartRateMonitor = () => {
  return async (dispatch: Dispatch<any>) => {
    const bluetoothService = BluetoothServiceSingleton.getBluetoothService();
    await bluetoothService.stopListeningForHeartRateData();
    // do not stop connected apps here
    dispatch(disconnectHrDevice());
  };
};

const stubDevices = [
  {
    id: 'E9:9F:A6:57:D3:51',
    localName: 'HW702A-0581125',
    manufacturerData: 'Bf8BZAYA',
    mtu: 23,
    name: 'HW702A-0581125',
    serviceUUIDs: ['0000180d-0000-1000-8000-00805f9b34fb'],
  },
  {
    id: 'E5:8F:46:8F:A6:19',
    localName: 'KAPz0-ben459',
    manufacturerData: '156gds',
    mtu: 12,
    name: 'SOME-00d-NAME',
    serviceUUIDs: ['0000180d-0000-1000-8000-00805f9b34fb'],
  },
  {
    id: 'E9:9F:A6:57:D3:52',
    localName: 'GW702A-0581125',
    manufacturerData: 'Bf8BHwYA',
    mtu: 23,
    name: 'GW702A-0581125',
    serviceUUIDs: ['0000180d-0000-1000-8000-00805f9b34fb'],
  },
  {
    id: 'E5:8F:46:8F:A6:29',
    localName: 'XAPz0-no-name',
    manufacturerData: '156gds',
    mtu: 12,
    name: 'XAPz0-no-name',
    serviceUUIDs: ['0000180d-0000-1000-8000-00805f9b34fb'],
  },
];
